import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { inventoryService } from '../../services/inventoryService';
import { 
  FiPlus, FiEdit, FiTrash2, FiPackage, FiAlertTriangle, 
  FiCheckCircle, FiSearch, FiEye 
} from 'react-icons/fi';

const InventoryList = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsData, categoriesData, suppliersData] = await Promise.all([
        inventoryService.getAll(),
        inventoryService.getCategories(),
        inventoryService.getSuppliers()
      ]);
      setItems(itemsData || []);
      setCategories(categoriesData || []);
      setSuppliers(suppliersData || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await inventoryService.delete(id);
        await loadData();
      } catch (error) {
        alert('Error deleting item: ' + error.message);
      }
    }
  };

  const getCategoryName = (id) => {
    if (!id) return 'Tanpa Kategori';
    const cat = categories.find(c => c.id === id);
    return cat ? cat.name : 'Tanpa Kategori';
  };

  const getSupplierName = (id) => {
    if (!id) return 'Tanpa Supplier';
    const sup = suppliers.find(s => s.id === id);
    return sup ? sup.name : 'Tanpa Supplier';
  };

  const getStockStatus = (item) => {
    const stock = item.stock || 0;
    const minStock = item.minStock || 0;
    
    if (stock <= 0) return { label: 'Out of Stock', color: 'bg-red-100 text-red-800', icon: FiAlertTriangle };
    if (stock <= minStock * 0.5) return { label: 'Critical', color: 'bg-red-100 text-red-800', icon: FiAlertTriangle };
    if (stock <= minStock) return { label: 'Low Stock', color: 'bg-yellow-100 text-yellow-800', icon: FiAlertTriangle };
    return { label: 'In Stock', color: 'bg-green-100 text-green-800', icon: FiCheckCircle };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredItems = Array.isArray(items) 
    ? items.filter(item => {
        const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              item.sku?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || item.categoryId === filterCategory;
        const stock = item.stock || 0;
        const minStock = item.minStock || 0;
        const matchesStatus = filterStatus === 'all' || 
                              (filterStatus === 'low' && stock <= minStock) ||
                              (filterStatus === 'critical' && stock <= minStock * 0.5) ||
                              (filterStatus === 'ok' && stock > minStock);
        return matchesSearch && matchesCategory && matchesStatus;
      })
    : [];

  const totalItems = Array.isArray(items) ? items.length : 0;
  const lowStockItems = Array.isArray(items) 
    ? items.filter(i => (i.stock || 0) <= (i.minStock || 0) && (i.stock || 0) > 0).length 
    : 0;
  const criticalItems = Array.isArray(items) 
    ? items.filter(i => (i.stock || 0) <= (i.minStock || 0) * 0.5 && (i.stock || 0) > 0).length 
    : 0;
  const outOfStockItems = Array.isArray(items) 
    ? items.filter(i => (i.stock || 0) <= 0).length 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading inventory...</p>
      </div>
    );
  }

  return (
    <div>
     
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Inventory</h1>
        <Link
          to="/inventory/new"
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <FiPlus size={20} />
          <span>Add Item</span>
        </Link>
      </div>

      
      {/* STATS CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">Total Items</p>
          <p className="text-lg font-bold text-[var(--text-primary)]">{totalItems}</p>
        </div>
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">Low Stock</p>
          <p className="text-lg font-bold text-yellow-600">{lowStockItems}</p>
        </div>
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">Critical</p>
          <p className="text-lg font-bold text-red-600">{criticalItems}</p>
        </div>
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">Out of Stock</p>
          <p className="text-lg font-bold text-red-600">{outOfStockItems}</p>
        </div>
      </div>

   
      {/* SEARCH & FILTER */}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={16} />
          <input
            type="text"
            placeholder="Search by name or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-1.5 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="ok">In Stock</option>
          <option value="low">Low Stock</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      
      {/* TABLE */}
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-sm border border-[var(--border-color)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--bg-secondary)]">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Item & Foto
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  SKU
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Category
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Stock
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Buy
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Sell
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Status
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-[var(--text-secondary)]">
                    <FiPackage size={40} className="mx-auto mb-2 opacity-50" />
                    <p>No items found</p>
                    <p className="text-sm">Click "Add Item" to create one</p>
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const stockStatus = getStockStatus(item);
                  const StatusIcon = stockStatus.icon;
                  return (
                    <tr key={item.id} className="border-t border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-colors">
                      
                      {/*ITEM & FOTO*/}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center space-x-3">
                          {item.photo ? (
                            <img
                              src={item.photo}
                              alt={item.name}
                              className="w-7 h-7 object-cover rounded-lg border border-[var(--border-color)]"
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-sm border border-[var(--border-color)]">
                              {item.name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="font-medium text-[var(--text-primary)]">{item.name}</span>
                        </div>
                      </td>

                      {/*SKU*/}
                      <td className="px-4 py-2.5 text-sm text-[var(--text-secondary)] font-mono">
                        {item.sku}
                      </td>

                      {/* --- CATEGORY --- */}
                      <td className="px-4 py-2.5 text-sm text-[var(--text-secondary)]">
                        {getCategoryName(item.categoryId)}
                      </td>

                      {/*STOCK*/}
                      <td className="px-4 py-2.5 text-sm text-center text-[var(--text-primary)]">
                        {item.stock} {item.unit}
                      </td>

                      {/*BUY PRICE*/}
                      <td className="px-4 py-2.5 text-sm text-right text-[var(--text-secondary)]">
                        {formatCurrency(item.buyPrice || 0)}
                      </td>

                      {/*SELL PRICE*/}
                      <td className="px-4 py-2.5 text-sm text-right text-[var(--text-secondary)]">
                        {formatCurrency(item.sellPrice || 0)}
                      </td>

                      {/*STATUS*/}
                      <td className="px-4 py-2.5 text-center">
                        <span className={`inline-flex items-center space-x-1 px-2 py-0.5 text-xs font-medium rounded-full ${stockStatus.color}`}>
                          <StatusIcon size={12} />
                          <span>{stockStatus.label}</span>
                        </span>
                      </td>

                      {/*ACTIONS*/}
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <Link
                            to={`/inventory/${item.id}`}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="View Details"
                          >
                            <FiEye size={16} />
                          </Link>
                          <Link
                            to={`/inventory/edit/${item.id}`}
                            className="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded transition-colors"
                            title="Edit"
                          >
                            <FiEdit size={16} />
                          </Link>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InventoryList;